import { generateHTML, type JSONContent } from '@tiptap/core';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { StarterKit } from '@tiptap/starter-kit';
import Ai from '@tiptap-pro/extension-ai';
import Emoji from '@tiptap-pro/extension-emoji';

import { ColorExtended } from './_extensions/Color';
import FontSize from './_extensions/FontSize';

import type { ExtensionConfig } from './_extensions/types';

export const tipTapToHTML = (
    tipTapJSON: JSONContent,
    inheritSize = false,
    extensionConfig: ExtensionConfig = {},
) => {
    const extensions = [
        ColorExtended,
        FontSize.configure({
            overrideFontSize: extensionConfig.fontSize,
        }),
        TextStyle,
        Highlight.configure({
            multicolor: true,
            HTMLAttributes: {
                class: 'fun-p-1 fun-rounded-md',
            },
        }),
        Link.configure({
            openOnClick: false,
            HTMLAttributes: {
                class: '*:fun-underline',
            },
        }),
        Underline,
        StarterKit.configure({
            blockquote: {
                HTMLAttributes: {
                    class: 'fun-border-l-4 fun-border-gray-300 fun-pl-2',
                },
            },
            horizontalRule: {
                HTMLAttributes: {
                    class: 'fun-border-current fun-opacity-20',
                },
            },
            orderedList: {
                HTMLAttributes: {
                    class: 'fun-list-decimal fun-list-inside fun-ml-6',
                },
            },
            bulletList: {
                HTMLAttributes: {
                    class: 'fun-list-disc fun-list-inside fun-ml-6',
                },
            },
            listItem: {
                HTMLAttributes: {
                    class: '[&>p]:fun-inline',
                },
            },
            bold: {
                HTMLAttributes: {
                    class: 'fun-font-bold',
                },
            },
        }),
        Emoji.configure({
            HTMLAttributes: {
                class: 'fun-font-sans fun-inline-block [&>img]:fun-h-[1em]', // Includes "Apple Color Emoji" font, which is necessary for emojis to render correctly
            },
        }),
        Mention.configure({
            renderHTML({ node }) {
                return `${node.attrs.label}`;
            },
        }),
        Ai,
    ];

    if (inheritSize) {
        // Remove FontSize extension
        extensions.splice(1, 1);
    }

    // TipTap and ProseMirror treat empty or whitespace-only paragraphs as non-essential, so they are omitted from the output.
    // This can lead to missing empty lines, so we have to replace those with a non-breaking space character
    tipTapJSON.content?.forEach((node) => {
        if (node?.content) {
            // Filter out empty text nodes (TipTap throws "Empty text nodes are not allowed" for text: "")
            node.content = node.content.filter((child) => {
                if (child.type === 'text' && !child.text) {
                    return false;
                }

                return true;
            });

            node.content.forEach((child) => {
                if (child.text) {
                    // Check if the text consists of only whitespace characters
                    if (/^\s*$/.test(child.text)) {
                        child.text = '\u00a0';
                    }

                    // Replace multiple spaces with non-breaking spaces
                    child.text = child.text.replace(/ {2,}/g, (space) => {
                        return space.replace(/ /g, '\u00a0');
                    });
                }
            });
        }
    });

    return generateHTML(tipTapJSON, extensions);
};
